import React       from 'react';
import type { PlanSourceResult } from '@models/map/dxfTypes';

/** Fenêtre visible du plan, en pixels de l'aperçu : c'est le `viewBox` du `<svg>`. */
export type PlanView = { x: number; y: number; w: number; h: number };

export type PlanPoint = { x: number; y: number };

const ZOOM_STEP = 1.2;
const BUTTON_ZOOM_STEP = 1.5;

/** Bornes du zoom, rapportées à la largeur de l'aperçu. */
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 40;

export function fullView(source: Pick<PlanSourceResult, 'width' | 'height'>): PlanView {
	return { x: 0, y: 0, w: source.width, h: source.height };
}

/**
 * Caméra d'un plan affiché en `<svg viewBox>` : molette, glisser, boutons de zoom.
 *
 * Les conversions écran → plan passent par `getScreenCTM()` : le `viewBox` est rendu en
 * `xMidYMid meet`, donc un simple rapport de largeurs se décale dès que la fenêtre n'a pas le
 * format du plan.
 */
export function usePlanViewport(source: Pick<PlanSourceResult, 'width' | 'height'>, opening: () => PlanView) {
	const [view, setView] = React.useState(opening);
	const [panning, setPanning] = React.useState<{ x: number; y: number } | null>(null);
	const svgRef = React.useRef<SVGSVGElement>(null);

	/** Pixels écran par pixel du plan. */
	const screenScale = (current: PlanView): number => {
		const svg = svgRef.current;
		const ctm = svg?.getScreenCTM?.();
		if (ctm?.a) return ctm.a;

		const box = svg?.getBoundingClientRect();

		return box?.width ? box.width / current.w : 1;
	};

	const toPlan = (clientX: number, clientY: number): PlanPoint | null => {
		const svg = svgRef.current;
		if (!svg) return null;

		const ctm = svg.getScreenCTM?.();
		if (ctm) {
			const point = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());

			return { x: point.x, y: point.y };
		}

		const box = svg.getBoundingClientRect();
		if (!box.width || !box.height) return null;

		return {
			x: view.x + ((clientX - box.left) / box.width) * view.w,
			y: view.y + ((clientY - box.top) / box.height) * view.h,
		};
	};

	/** Zoome d'un facteur (>1 = dézoome) en gardant `anchor` immobile, le centre par défaut. */
	const zoomBy = (factor: number, anchor?: PlanPoint | null) => {
		setView(previous => {
			const zoom = source.width / (previous.w * factor);
			if (zoom < MIN_ZOOM || zoom > MAX_ZOOM) return previous;

			const at = anchor ?? { x: previous.x + previous.w / 2, y: previous.y + previous.h / 2 };

			return {
				x: at.x - (at.x - previous.x) * factor,
				y: at.y - (at.y - previous.y) * factor,
				w: previous.w * factor,
				h: previous.h * factor,
			};
		});
	};

	const onWheel = (event: React.WheelEvent<SVGSVGElement>) => {
		zoomBy(event.deltaY > 0 ? ZOOM_STEP : 1 / ZOOM_STEP, toPlan(event.clientX, event.clientY));
	};

	const startPan = (event: React.PointerEvent) => setPanning({ x: event.clientX, y: event.clientY });

	/** Renvoie vrai quand le geste était un déplacement, pour que l'appelant s'arrête là. */
	const movePan = (event: React.PointerEvent): boolean => {
		if (!panning) return false;

		const scale = screenScale(view);
		setView(previous => ({
			...previous,
			x: previous.x - (event.clientX - panning.x) / scale,
			y: previous.y - (event.clientY - panning.y) / scale,
		}));
		setPanning({ x: event.clientX, y: event.clientY });

		return true;
	};

	const endPan = () => setPanning(null);

	return {
		view,
		setView,
		svgRef,
		panning: null !== panning,
		toPlan,
		onWheel,
		startPan,
		movePan,
		endPan,
		zoomIn: () => zoomBy(1 / BUTTON_ZOOM_STEP),
		zoomOut: () => zoomBy(BUTTON_ZOOM_STEP),
		fit: () => setView(opening()),
		zoomPercent: Math.round((source.width / view.w) * 100),
	};
}
